import React from 'react';

const StatCard = ({
    title,
    value,
    color,
    icon,
    onClick,
    loading,
    trend, // Optional: { value: number, direction: 'up' | 'down' | 'stable' }
    subtitle, // Optional: additional context
    pulse // Optional: boolean for critical values
}) => {
    const colorMap = {
        blue: {
            text: 'text-blue-400',
            bgBar: 'bg-blue-500',
            icon: 'text-blue-500',
            bg: 'bg-blue-900/10',
            border: 'border-blue-800/30'
        },
        yellow: {
            text: 'text-yellow-400',
            bgBar: 'bg-yellow-500',
            icon: 'text-yellow-500',
            bg: 'bg-yellow-900/10',
            border: 'border-yellow-800/30'
        },
        red: {
            text: 'text-red-500',
            bgBar: 'bg-red-500',
            icon: 'text-red-500',
            bg: 'bg-red-900/10',
            border: 'border-red-800/30'
        },
        green: {
            text: 'text-green-500',
            bgBar: 'bg-green-500',
            icon: 'text-green-500',
            bg: 'bg-green-900/10',
            border: 'border-green-800/30'
        },
        purple: {
            text: 'text-purple-500',
            bgBar: 'bg-purple-500',
            icon: 'text-purple-500',
            bg: 'bg-purple-900/10',
            border: 'border-purple-800/30'
        },
        orange: {
            text: 'text-orange-500',
            bgBar: 'bg-orange-500',
            icon: 'text-orange-500',
            bg: 'bg-orange-900/10',
            border: 'border-orange-800/30'
        },
    };

    const theme = colorMap[color] || colorMap.blue;

    const getTrendIcon = () => {
        if (!trend) return null;
        const { direction, value } = trend;

        if (direction === 'up') {
            return (
                <span className="inline-flex items-center text-xs text-green-400">
                    <svg className="w-3 h-3 mr-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                    </svg>
                    {value}%
                </span>
            );
        }
        if (direction === 'down') {
            return (
                <span className="inline-flex items-center text-xs text-red-400">
                    <svg className="w-3 h-3 mr-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                    </svg>
                    {value}%
                </span>
            );
        }
        return (
            <span className="inline-flex items-center text-xs text-gray-400">
                <svg className="w-3 h-3 mr-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14" />
                </svg>
                stable
            </span>
        );
    };

    if (loading) {
        return (
            <div className="bg-gray-800 p-6 rounded-lg border border-gray-700 shadow-lg animate-pulse h-32">
                <div className="h-4 bg-gray-700 rounded w-1/3 mb-4"></div>
                <div className="h-8 bg-gray-700 rounded w-1/2"></div>
            </div>
        );
    }

    return (
        <div
            className={`bg-gray-800 p-5 rounded-lg border border-gray-700 shadow-lg relative overflow-hidden group transition-all duration-200 ${onClick ? 'cursor-pointer hover:border-gray-600 hover:shadow-xl' : ''
                } ${pulse ? 'ring-2 ring-red-500/50 animate-pulse' : ''}`}
            onClick={onClick}
            role={onClick ? 'button' : undefined}
            tabIndex={onClick ? 0 : undefined}
            onKeyDown={(e) => {
                if (onClick && e.key === 'Enter') onClick();
            }}
        >
            {/* Background Icon */}
            <div className="absolute top-0 right-0 p-2 opacity-10 group-hover:opacity-20 transition-opacity">
                {icon}
            </div>

            {/* Title */}
            <div className="flex items-center justify-between mb-2">
                <h3 className="text-gray-400 text-xs uppercase tracking-wider font-medium">{title}</h3>
                {trend && (
                    <div className="flex items-center">
                        {getTrendIcon()}
                    </div>
                )}
            </div>

            {/* Value */}
            <div className={`text-3xl font-bold ${theme.text}`}>
                {typeof value === 'number' ? value.toLocaleString() : value}
            </div>

            {/* Subtitle */}
            {subtitle && (
                <div className="text-xs text-gray-500 mt-1">{subtitle}</div>
            )}

            {/* Bottom Bar */}
            <div className="mt-3 h-1 w-full bg-gray-700 rounded-full overflow-hidden">
                <div className={`h-full ${theme.bgBar} transition-all duration-500`} style={{ width: '100%' }}></div>
            </div>

            {/* Hover Arrow */}
            {onClick && (
                <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                </div>
            )}
        </div>
    );
};

export default StatCard;
